import React, { useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from 'react-router-dom';
import ChannelSelectModal from '../ui/ChannelSelectModal';
import { CrmBadge, CrmButton, CrmTooltip } from '../ui';
import { CrmEntityListPage } from '../listing';
import {
  sendQuotationEmailToCustomer,
  sendQuotationWhatsAppToCustomer,
  updateQuotationStatus,
} from '../../services/quotationService';
import { useSettings } from '../../context/SettingsContext';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatDate } from '../../utils/dateFormatter';
import { formatStatusLabel, normalizeStatusValue } from '../../utils/statusFormatter';

const statusOptions = ['pending', 'approved', 'rejected', 'converted'];
const toneForStatus = (status) => ({ approved: 'success', rejected: 'danger', converted: 'info', pending: 'warning' }[normalizeStatusValue(status)] || 'neutral');
const contactName = (row) => `${row?.first_name || ''} ${row?.last_name || ''}`.trim() || row?.lead_name || '—';

function QuotationTable({ quotations = [], loading = false, reload, onNotify }) {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [openParents, setOpenParents] = useState({});
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [channelModalOpen, setChannelModalOpen] = useState(false);
  const [notificationRows, setNotificationRows] = useState([]);

  const groups = useMemo(() => {
    const map = new Map();
    quotations.forEach((row) => {
      const parentId = row.parent_id || row.id;
      if (!map.has(parentId)) map.set(parentId, []);
      map.get(parentId).push(row);
    });
    return [...map.entries()].map(([parentId, rows]) => ({ parentId, rows: [...rows].sort((a, b) => Number(b.version || 1) - Number(a.version || 1)) }));
  }, [quotations]);

  const displayRows = useMemo(() => groups.flatMap((group) => {
    const latest = { ...group.rows[0], _parentKey: group.parentId, _versionCount: group.rows.length, _isVersion: false };
    if (!openParents[group.parentId]) return [latest];
    return [latest, ...group.rows.slice(1).map((row) => ({ ...row, _parentKey: group.parentId, _versionCount: group.rows.length, _isVersion: true }))];
  }), [groups, openParents]);

  const handleStatusChange = async (id, status) => {
    try {
      await updateQuotationStatus(id, status);
      await reload?.();
      onNotify?.('Quotation status updated', 'success');
    } catch (error) {
      onNotify?.(error?.response?.data?.error || 'Failed to update quotation status', 'error');
    } finally {
      setEditingStatusId(null);
    }
  };

  const sendNotifications = async ({ sendEmail = true, sendWhatsApp = false } = {}) => {
    const tasks = [];
    if (sendEmail) tasks.push(...notificationRows.map((row) => sendQuotationEmailToCustomer(row.id)));
    if (sendWhatsApp) tasks.push(...notificationRows.map((row) => sendQuotationWhatsAppToCustomer(row.id)));
    const results = await Promise.allSettled(tasks);
    const success = results.filter((result) => result.status === 'fulfilled').length;
    const failed = results.length - success;
    onNotify?.(failed ? `Sent ${success}; ${failed} request(s) failed` : `Sent ${success} quotation notification(s)`, failed ? 'warning' : 'success');
  };

  const columns = useMemo(() => [
    {
      id: 'quotation_number', label: 'Quotation #', width: 190, minWidth: 150, maxWidth: 300,
      hideable: false, defaultPinned: true, pinnable: true, stopRowClick: true,
      accessor: (row) => row.quotation_number || '',
      renderCell: (row) => (
        <span className="crm-row-action-group">
          {row._versionCount > 1 && !row._isVersion ? (
            <CrmTooltip content={openParents[row._parentKey] ? 'Hide versions' : 'Show versions'}>
              <CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => setOpenParents((previous) => ({ ...previous, [row._parentKey]: !previous[row._parentKey] }))} aria-label="Toggle quotation versions">
                {openParents[row._parentKey] ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </CrmButton>
            </CrmTooltip>
          ) : null}
          <button type="button" className="crm-table-primary-link" onClick={() => navigate(`/quotations/${row.id}`)}>{row._isVersion ? `↳ ${row.quotation_number || '—'}` : row.quotation_number || '—'}</button>
        </span>
      ),
    },
    { id: 'customer', label: 'Contact', width: 210, minWidth: 150, maxWidth: 360, accessor: contactName },
    { id: 'quotation_date', label: 'Date', width: 140, minWidth: 120, maxWidth: 210, type: 'date', filterable: true, accessor: (row) => row.quotation_date, renderCell: (row) => formatDate(row.quotation_date) || '—' },
    { id: 'total_amount', label: 'Total', width: 150, minWidth: 120, maxWidth: 220, type: 'currency', accessor: (row) => Number(row.total_amount || 0), renderCell: (row) => formatCurrency(row.total_amount, settings?.currency_code) },
    { id: 'version', label: 'Version', width: 100, minWidth: 90, maxWidth: 140, type: 'number', accessor: (row) => Number(row.version || 1), renderCell: (row) => `v${Number(row.version || 1)}` },
    {
      id: 'status', label: 'Status', width: 150, minWidth: 125, maxWidth: 210, filterable: true, stopRowClick: true,
      accessor: (row) => normalizeStatusValue(row.status),
      renderCell: (row) => editingStatusId === row.id ? (
        <select className="status-select-inline" value={normalizeStatusValue(row.status)} autoFocus onBlur={() => setEditingStatusId(null)} onChange={(event) => handleStatusChange(row.id, event.target.value)}>
          {statusOptions.map((status) => <option key={status} value={status}>{formatStatusLabel(status)}</option>)}
        </select>
      ) : <button type="button" className="crm-badge-button" onClick={() => setEditingStatusId(row.id)}><CrmBadge tone={toneForStatus(row.status)}>{formatStatusLabel(row.status)}</CrmBadge></button>,
    },
    { id: 'row_actions', label: 'Actions', width: 90, minWidth: 90, maxWidth: 120, hideable: false, sortable: false, searchable: false, renderCell: (row) => <CrmTooltip content="View quotation"><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => navigate(`/quotations/${row.id}`)} aria-label="View quotation"><VisibilityOutlinedIcon /></CrmButton></CrmTooltip> },
  ], [editingStatusId, navigate, openParents, settings?.currency_code]);

  const filters = useMemo(() => [
    { id: 'quotation_date', columnId: 'quotation_date', label: 'Date', type: 'date-range', quick: true, getValue: (row) => row.quotation_date },
    { id: 'status', columnId: 'status', label: 'Status', type: 'select', quick: true, options: statusOptions.map((value) => ({ value, label: formatStatusLabel(value) })), getValue: (row) => normalizeStatusValue(row.status) },
  ], []);

  return (
    <>
      <CrmEntityListPage
        moduleKey="quotations"
        title="Quotations"
        itemLabel="quotation"
        items={displayRows}
        columns={columns}
        filterDefinitions={filters}
        initialSort={{ columnId: 'quotation_date', direction: 'desc' }}
        loading={loading}
        onCreate={() => navigate('/quotation-create')}
        createLabel="Create quotation"
        onSettings={() => navigate('/quotations-settings')}
        onRefresh={reload}
        onRowClick={(row) => navigate(`/quotations/${row.id}`)}
        renderBulkActions={(selectedRows) => <CrmButton size="small" startIcon={<SendOutlinedIcon />} onClick={() => { setNotificationRows(selectedRows); setChannelModalOpen(true); }}>Send</CrmButton>}
      />
      <ChannelSelectModal open={channelModalOpen} onClose={() => setChannelModalOpen(false)} title="Send Quotation Notifications" subtitle="Choose channels for selected quotations" defaultEmail defaultWhatsApp confirmLabel="Send Notifications" onConfirm={async (selection) => { setChannelModalOpen(false); await sendNotifications(selection); }} />
    </>
  );
}

export default QuotationTable;
